import React, { useState, useContext } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { ToastContext } from './ToastProvider';

const RemittanceConfirmationModal = ({ delivery, onClose, onConfirm }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [notes, setNotes] = useState('');
  const { notify } = useContext(ToastContext);

  // Calculate remittance details
  const codAmount = Number(delivery?.codAmount || 0);
  const totalRemitted = delivery?.remittances?.reduce((sum, r) => sum + Number(r.amount || 0), 0) || 0;
  const remainingAmount = codAmount - totalRemitted;
  const remittanceCount = delivery?.remittances?.length || 0;

  const handleConfirmRemittance = async () => {
    if (remainingAmount > 0) {
      notify('Cannot confirm remittance: full COD amount not yet remitted', { type: 'error' });
      return;
    }

    setIsProcessing(true);
    try {
      // Call the Cloud Function to confirm remittance and credit wallet
      const confirmRemittance = httpsCallable(functions, 'confirmRemittanceAndCreditWallet');
      const result = await confirmRemittance({
        deliveryId: delivery.id,
        action: 'confirm',
        notes: notes.trim()
      });

      if (result.data?.ok) {
        notify('Remittance confirmed and rider wallet credited successfully!', { type: 'success' });
        onConfirm(result.data);
      } else {
        notify('Failed to confirm remittance', { type: 'error' });
      }
    } catch (error) {
      console.error('Error confirming remittance:', error);
      notify(error?.message || 'Failed to confirm remittance', { type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRejectRemittance = async () => {
    if (!notes.trim()) {
      notify('Please provide a reason for rejection', { type: 'error' });
      return;
    }

    setIsProcessing(true);
    try {
      // Call the Cloud Function to reject remittance
      const rejectRemittance = httpsCallable(functions, 'confirmRemittanceAndCreditWallet');
      const result = await rejectRemittance({
        deliveryId: delivery.id,
        action: 'reject',
        notes: notes.trim()
      });

      if (result.data?.ok) {
        notify('Remittance rejected successfully', { type: 'success' });
        onConfirm(result.data);
      } else {
        notify('Failed to reject remittance', { type: 'error' });
      }
    } catch (error) {
      console.error('Error rejecting remittance:', error);
      notify(error?.message || 'Failed to reject remittance', { type: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1200]">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Confirm COD Remittance</h2>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Delivery Information */}
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Delivery Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <span className="text-sm font-medium text-gray-600">Order ID:</span>
              <p className="text-gray-900">{delivery.orderId || 'N/A'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Customer:</span>
              <p className="text-gray-900">{delivery.buyerName || 'Unknown'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Rider:</span>
              <p className="text-gray-900">{delivery.riderName || 'Unassigned'}</p>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-600">Total COD Amount:</span>
              <p className="text-gray-900 font-semibold">₱{codAmount.toFixed(2)}</p>
            </div>
          </div>
        </div>

        {/* Remittance Summary */}
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">Remittance Summary</h3>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-blue-700">Total COD Amount:</span>
              <span className="font-semibold text-blue-900">₱{codAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Total Remitted:</span>
              <span className="font-semibold text-blue-900">₱{totalRemitted.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Remaining Amount:</span>
              <span className={`font-semibold ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                ₱{remainingAmount.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-blue-700">Number of Remittances:</span>
              <span className="font-semibold text-blue-900">{remittanceCount}</span>
            </div>
          </div>
        </div>

        {/* Remittance Details */}
        {delivery.remittances && delivery.remittances.length > 0 && (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Remittance Details</h3>
            <div className="space-y-3">
              {delivery.remittances.map((remittance, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-md">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Remittance #{index + 1}</span>
                    <p className="text-sm text-gray-500">
                      {remittance.timestamp?.toDate?.()?.toLocaleString() || 'Unknown date'}
                    </p>
                    {remittance.notes && (
                      <p className="text-sm text-gray-600 mt-1">{remittance.notes}</p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-semibold text-gray-900">
                      ₱{Number(remittance.amount).toFixed(2)}
                    </span>
                    <p className="text-xs text-gray-500">
                      by {remittance.cashierId || 'Rider'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notes Input */}
        <div className="mb-6">
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
            Confirmation Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Add any notes about this confirmation..."
            disabled={isProcessing}
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
          
          {remainingAmount > 0 ? (
            <button
              onClick={handleRejectRemittance}
              disabled={isProcessing || !notes.trim()}
              className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Reject Remittance'}
            </button>
          ) : (
            <button
              onClick={handleConfirmRemittance}
              disabled={isProcessing}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
            >
              {isProcessing ? 'Processing...' : 'Confirm & Credit Wallet'}
            </button>
          )}
        </div>

        {/* Warning Messages */}
        {remainingAmount > 0 && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="text-sm text-yellow-800">
              <strong>Warning:</strong> Full COD amount has not been remitted yet. 
              You can only reject this remittance at this time.
            </div>
          </div>
        )}

        {remainingAmount <= 0 && (
          <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
            <div className="text-sm text-green-800">
              <strong>Ready for Confirmation:</strong> Full COD amount has been remitted. 
              You can now confirm this remittance and credit the rider's wallet.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RemittanceConfirmationModal;
